import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Power, Users } from 'lucide-react';
import { useMuqriSession } from '@core/session/useMuqriSession';
import type { SessionMode } from '@core/session/turns';
import { VoiceNoteRecorder } from '@/components/VoiceNoteRecorder';
import { PracticeSummary } from '@/components/PracticeSummary';
import { useMyProfile, saveTeachingLanguages } from '@core/firebase/profile';
import { LANGUAGES, endonym, type LanguageCode } from '@core/i18n/languages';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Toggle } from '@/components/Toggle';
import { GenderPrompt } from '@/components/GenderPrompt';
import { cn } from '@/lib/cn';
import {
  CardTitle, ChoicePills, EmptyState, LimePill, Notice, PageHeader, SoftCard, StatTile, pill,
} from '@/components/PageKit';

/**
 * Running a session.
 *
 * The lifecycle — recovering today's session on mount, the beat that keeps it
 * listed, who may take the turn — lives in the shared useMuqriSession hook,
 * which the mobile screen also uses. This page only draws.
 *
 * Open is a STATE the معلم is in, so it is a switch rather than two
 * full-width buttons, one of which was irreversible.
 */
export default function MuqriSessionPage() {
  const { identity } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const profile = useMyProfile(identity?.userId ?? null);
  const [languagesFailed, setLanguagesFailed] = useState(false);

  const toggleLanguage = (code: LanguageCode) => {
    const held = profile.teachingLanguages;
    const next = held.includes(code) ? held.filter((c) => c !== code) : [...held, code];
    setLanguagesFailed(false);
    // Written straight through: the profile snapshot is what the chips read.
    void saveTeachingLanguages(next).catch(() => setLanguagesFailed(true));
  };

  const {
    sessionIdValue, isOpen, mode, setMode, present, participants,
    currentReciterId, failure, open, close, admit, handOver,
  } = useMuqriSession({
    muqriId: identity?.userId ?? '',
    muqriName: identity?.displayName ?? '',
    // م-7-02: copied onto the session at open, because a pupil may not read
    // this teacher's own document.
    languages: profile.teachingLanguages,
    // Sessions are same-gender; carried on the session for the same reason
    // the languages are.
    gender: profile.gender,
  });

  const inSession = participants.filter((id) => id !== identity?.userId).length;
  const recitingName = present.find((p) => p.studentId === currentReciterId)?.name ?? null;
  const count = (n: number) => new Intl.NumberFormat('en-US').format(n);

  /*
   * Ending a session is irreversible for everyone waiting in it — they all
   * leave the queue. Every way of ending it (the switch, or «أنهِ الجلسة»)
   * asks first, as the phone does (board t3).
   */
  const [confirmingEnd, setConfirmingEnd] = useState(false);

  // A session opened with no gender on it matches no pupil: the list filters
  // on muqriGender and admitStudent refuses an unknown one. Opening waits for
  // the teacher to say; closing never does.
  const needsGender = !profile.gender && !profile.loading && !isOpen;
  const toggle = (next: boolean) => {
    if (next === isOpen) return;
    if (next && needsGender) return;
    if (next) { void open(); return; }
    setConfirmingEnd(true);
  };
  const endSession = () => {
    setConfirmingEnd(false);
    void close();
  };

  const kind = t(mode === 'private' ? 'muqri.kindPrivate' : 'muqri.kindHalaqa');

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      <PageHeader
        title={t('muqri.session')}
        actions={isOpen ? (
          <button
            type="button"
            data-testid="end-session"
            onClick={() => setConfirmingEnd(true)}
            className={cn(pill.base, pill.md, pill.dangerOutline)}
          >
            <Power className="h-4 w-4" />
            {t('muqri.endSession')}
          </button>
        ) : undefined}
      />

      {confirmingEnd && (
        <div
          data-testid="end-session-confirm"
          role="alertdialog"
          aria-labelledby="end-session-title"
          className="space-y-3 rounded-[22px] bg-danger/10 p-5"
        >
          <p id="end-session-title" className="text-lg font-semibold text-ink">{t('muqri.endConfirmTitle')}</p>
          <p className="text-[15px] text-helper">{t('muqri.endConfirmBody', { n: count(present.length) })}</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="end-session-yes"
              onClick={endSession}
              className={cn(pill.base, pill.md, pill.danger)}
            >
              {t('muqri.endConfirm')}
            </button>
            <button
              type="button"
              data-testid="end-session-no"
              onClick={() => setConfirmingEnd(false)}
              className={cn(pill.base, pill.md, pill.quiet)}
            >
              {t('muqri.keepOpen')}
            </button>
          </div>
        </div>
      )}

      <div className={cn('grid items-start gap-6', isOpen ? 'lg:grid-cols-2' : 'max-w-3xl')}>
        <div className="space-y-6">
          <SoftCard testId="session-status" className="space-y-4">
            <CardTitle
              icon={<Power />}
              trailing={(
                <div className="flex shrink-0 items-center gap-3">
                  {isOpen ? (
                    <LimePill testId="session-status-label">{t('muqri.live')}</LimePill>
                  ) : (
                    <span data-testid="session-status-label" className="text-sm text-helper">
                      {t('muqri.statusClosed')}
                    </span>
                  )}
                  <Toggle
                    testId="session-toggle"
                    checked={isOpen}
                    disabled={needsGender}
                    onChange={toggle}
                    label={t('muqri.toggleLabel')}
                  />
                </div>
              )}
            >
              {isOpen ? kind : t('muqri.statusTitle')}
            </CardTitle>

            {needsGender && <GenderPrompt message={t('muqri.genderNeeded')} />}

            {/* The mode is written onto the session document at open time, so it
                is only choosable while closed: a control that silently does
                nothing to a running session is worse than no control. */}
            {!isOpen && (
              <ChoicePills
                value={mode}
                onChange={setMode}
                className="max-w-md"
                options={(['private', 'halaqa'] as SessionMode[]).map((m) => ({
                  value: m,
                  label: t(m === 'private' ? 'muqri.modePrivate' : 'muqri.modeHalaqa'),
                  testId: `mode-${m}`,
                }))}
              />
            )}
            <p className="text-sm text-helper">
              {isOpen && <>{t('muqri.queueCount', { n: count(present.length) })} · </>}
              {t(mode === 'private' ? 'muqri.modePrivateHint' : 'muqri.modeHalaqaHint')}
            </p>

            {/* م-7-02: declared while closed, because the list is copied onto the
                session at open time and a chip tapped mid-session would change
                nothing a pupil can see. */}
            {!isOpen && (
              <div className="space-y-2 border-t border-hair pt-4">
                <p className="text-sm font-medium text-ink">{t('profile.teachingLanguages')}</p>
                <div className="flex flex-wrap gap-2">
                  {LANGUAGES.map((language) => {
                    const on = profile.teachingLanguages.includes(language.code);
                    return (
                      <button
                        key={language.code}
                        type="button"
                        data-testid={`teaches-${language.code}`}
                        role="checkbox"
                        aria-checked={on}
                        onClick={() => toggleLanguage(language.code)}
                        className={cn(
                          pill.base, pill.sm,
                          on ? 'bg-lime text-accent-foreground' : pill.quiet,
                        )}
                      >
                        {on && <Check className="h-3.5 w-3.5" />}
                        {endonym(language.code)}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-helper">{t('profile.teachingLanguagesHint')}</p>
                {languagesFailed && (
                  <Notice tone="danger" testId="languages-failed">{t('profile.languagesFailed')}</Notice>
                )}
              </div>
            )}

            {isOpen && (
              <div data-testid="session-stats" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <StatTile testId="stat-waiting" value={count(present.length)} label={t('muqri.statWaiting')} />
                  <StatTile testId="stat-in-session" value={count(inSession)} label={t('muqri.statInSession')} />
                </div>

                {mode === 'halaqa' && (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p data-testid="reciting-now" className="text-[15px] text-ink">
                      {recitingName
                        ? t('muqri.recitingNow', { name: recitingName })
                        : t('muqri.recitingNobody')}
                    </p>
                    {/* Passing the turn is the one action that is not a state, and
                        it exists only in حلقة, where there are turns at all. */}
                    <button
                      type="button"
                      data-testid="pass-turn"
                      onClick={() => { void handOver(); }}
                      className={cn(pill.base, pill.md, pill.primary)}
                    >
                      {t('muqri.passTurn')}
                    </button>
                  </div>
                )}
              </div>
            )}

            {failure === 'open' && <Notice tone="danger" testId="open-failed">{t('muqri.openFailed')}</Notice>}
            {failure === 'close' && <Notice tone="danger" testId="close-failed">{t('muqri.closeFailed')}</Notice>}
            {failure === 'passTurn' && (
              <Notice tone="danger" testId="pass-turn-failed">{t('muqri.passTurnFailed')}</Notice>
            )}
          </SoftCard>

          {/* م-6-02: what the system noticed in this pupil's own practice, at
              the moment the معلم starts listening to them. */}
          {isOpen && currentReciterId && (
            <PracticeSummary studentId={currentReciterId} />
          )}

          {/*
            * م-3-04: a note is about a TURN, so it lives where the turn is and
            * only while somebody is taking one. Offered with no pupil named, the
            * microphone would be an invitation to record about whoever is next.
            */}
          {isOpen && currentReciterId && (
            <VoiceNoteRecorder
              studentId={currentReciterId}
              studentName={recitingName}
              sessionId={sessionIdValue}
            />
          )}
        </div>

        {/* Open, the queue sits beside the session: who is next, at a glance. */}
        {isOpen && (
          <SoftCard testId="session-queue" className="space-y-4">
            <CardTitle
              icon={<Users />}
              trailing={<span className="text-sm text-helper">{count(present.length)}</span>}
            >
              {t('muqri.queueHeading')}
            </CardTitle>

            {failure === 'admit' && <Notice tone="danger" testId="admit-failed">{t('muqri.admitFailed')}</Notice>}

            {present.length === 0 ? (
              <EmptyState
                testId="no-students"
                icon={<Users />}
                title={t('muqri.noStudents')}
                body={t('muqri.noStudentsBody')}
              />
            ) : (
              /*
               * Numbered in join order, the head of the queue on lime and its
               * «أدخِل» filled — the order a pupil was promised.
               */
              <ol className="divide-y divide-hair">
                {present.map((s, i) => (
                  <li
                    key={s.studentId}
                    data-testid={`queue-row-${s.studentId}`}
                    className="flex items-center gap-3 py-3"
                  >
                    <span
                      className={cn(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
                        i === 0 ? 'bg-lime text-accent-foreground' : 'bg-surface text-helper',
                      )}
                    >
                      {count(i + 1)}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-ink">{s.name}</span>
                    <button
                      type="button"
                      data-testid={`admit-${s.studentId}`}
                      onClick={() => {
                        void admit(s.studentId, () => navigate(`/call/${sessionIdValue}`));
                      }}
                      className={cn(pill.base, pill.sm, i === 0 ? pill.primary : pill.quiet)}
                    >
                      {t('muqri.admit')}
                    </button>
                  </li>
                ))}
              </ol>
            )}
          </SoftCard>
        )}
      </div>
    </div>
  );
}
